import { useAtomValue } from 'jotai';
import { VideoHTMLAttributes, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { isMobile } from '@growtherapy/analytics';

import { HideSelfViewButton, VideoThumbnail, VideoThumbnailProps } from '../../components';
import { isElementVisible } from '../../utils';
import {
  LocalMediaStatus,
  localAudioContextAtom,
  localVideoContextAtom,
  roomAtom,
  useLocalVideoComponent,
  useStoredAudioVideoSettings,
} from '../../video-sdk';
import { isSpeakingAtom, thumbnailPositionAtom } from '../../video-sdk/twilio';

function Video(props: VideoHTMLAttributes<HTMLVideoElement>) {
  const LocalVideo = useLocalVideoComponent();

  const videoRef = useRef<HTMLVideoElement>(null);
  const { delegator, status } = useAtomValue(localVideoContextAtom);
  const videoIsReady = delegator && status === LocalMediaStatus.READY;

  useEffect(() => {
    const element = videoRef.current;
    if (!videoIsReady || !element) {
      return;
    }

    delegator.attach(element);
    return () => {
      delegator.detach(element);
    };

    // Do not add the delegator to the dependency array as the delegator for Zoom
    // is unstable and changes depending on in-session vs. pre-session which can
    // cause issues
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoIsReady]);

  return <LocalVideo videoRef={videoRef} videoProps={props} />;
}

export type LocalAudioVideoThumbnailProps = Pick<
  VideoThumbnailProps,
  | 'className'
  | 'name'
  | 'isSettingsThumbnail'
  | 'pronouns'
  | 'showExpandVideoToggle'
  | 'helpText'
  | 'showSetPositionMenu'
  | 'videoClassName'
  | 'isSpeaking'
  | 'videoOffContent'
> & {
  shouldShowHideSelf?: boolean;
};

export function LocalAudioVideoThumbnail(props: LocalAudioVideoThumbnailProps) {
  // ⚠️ Do not set to false initially. Setting this to false initially can cause
  // the video to briefly render and dispatch an attach side-effect
  const [isVideoHidden, setIsVideoHidden] = useState<boolean>(true);
  const thumbnailContainerRef = useRef<HTMLDivElement>(null);
  const { delegator: localVideoDelegator, status: localVideoStatus } = useAtomValue(localVideoContextAtom);
  const { delegator: localAudioDelegator, status: localAudioStatus } = useAtomValue(localAudioContextAtom);

  const thumbnailPosition = useAtomValue(thumbnailPositionAtom);
  const { storedAudioVideoSettings, setStoredAudioVideoSettings } = useStoredAudioVideoSettings();
  const isMirrored = storedAudioVideoSettings?.videoIsMirrored ?? false;
  const isVideoEnabled = localVideoDelegator && localVideoStatus === LocalMediaStatus.READY;
  const isAudioEnabled = localAudioDelegator && localAudioStatus === LocalMediaStatus.READY;
  const isLoading = localVideoStatus === LocalMediaStatus.LOADING;
  const isSelfViewHidden = storedAudioVideoSettings?.selfViewIsHidden ?? false;
  const isMobileDevice = isMobile(navigator.userAgent);
  const isSpeaking = useAtomValue(isSpeakingAtom);
  const isInSession = !!useAtomValue(roomAtom);

  const toggleSelfViewSetting = useCallback(() => {
    setStoredAudioVideoSettings((settings) => ({
      ...settings,
      selfViewIsHidden: !settings.selfViewIsHidden,
    }));
  }, [setStoredAudioVideoSettings]);

  useLayoutEffect(
    function handleInitialVideoVisibility() {
      if (thumbnailContainerRef.current && isElementVisible(thumbnailContainerRef.current)) {
        setIsVideoHidden(false);
      }
    },
    [thumbnailContainerRef],
  );

  useEffect(function handleVideoVisibilityOnResize() {
    const abortController = new AbortController();
    function handleResize() {
      if (thumbnailContainerRef.current && !isElementVisible(thumbnailContainerRef.current)) {
        setIsVideoHidden(true);
      } else {
        setIsVideoHidden(false);
      }
    }

    window.addEventListener('resize', handleResize, {
      signal: abortController.signal,
    });

    return () => abortController.abort();
  }, []);

  const hideSelfViewButton = props.shouldShowHideSelf ? (
    <HideSelfViewButton
      isMobile={isMobileDevice}
      position={thumbnailPosition}
      isSelfViewHidden={isSelfViewHidden}
      isVideoEnabled={!!isVideoEnabled}
      isInSession={isInSession}
      onClick={toggleSelfViewSetting}
    />
  ) : null;

  return (
    <VideoThumbnail
      audioTransmissionEnabled={isAudioEnabled}
      containerRef={thumbnailContainerRef}
      videoTransmissionEnabled={isVideoEnabled && !isVideoHidden}
      videoComponent={Video}
      loadingVideo={isLoading}
      hideSelfViewButton={hideSelfViewButton}
      isMirrored={isMirrored}
      isMobile={isMobileDevice}
      isSelfViewHidden={isSelfViewHidden}
      isSpeaking={isSpeaking}
      {...props}
    />
  );
}
